import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  MenuItem,
  Alert,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';
import { ArrowBack, Save } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import projectsService from '../services/projectsService';
import useCompanySelector from '../hooks/useCompanySelector';

type ProjectStatus = 'planning' | 'active' | 'on_hold' | 'completed' | 'cancelled';
type ProjectPriority = 'low' | 'medium' | 'high' | 'critical';

interface ProjectForm {
  name: string;
  description: string;
  status: ProjectStatus;
  priority: ProjectPriority;
}

const statusOptions = [
  { value: 'planning', label: 'Planning' },
  { value: 'active', label: 'Active' },
  { value: 'on_hold', label: 'On Hold' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
];

const priorityOptions = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' },
];

// Normalize company id from project payload
const getCompanyIdFromProject = (p: any): string | null => {
  const c = p?.company;
  if (!c) return null;
  if (typeof c === 'string') return String(c);
  const id = c?._id || c?.id;
  return id ? String(id) : null;
};

const ProjectEdit: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useAuth();
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<ProjectForm>({
    name: '',
    description: '',
    status: 'planning',
    priority: 'medium',
  });
  // Company selection via centralized hook
  const { isAdmin, companies, selectedCompanyId, setSelectedCompanyId } = useCompanySelector();
  // Track project's current company to prefill selector
  const [initialCompanyId, setInitialCompanyId] = useState<string | null>(null);

  useEffect(() => {
    const fetchProject = async () => {
      try {
        if (!id) {
          setError('Missing project ID');
          setLoading(false);
          return;
        }
        const res = await projectsService.getById(id);
        const p = res.data.data;
        setInitialCompanyId(getCompanyIdFromProject(p));
        const statusValues = ['planning', 'active', 'on_hold', 'completed', 'cancelled'] as const;
        const priorityValues = ['low', 'medium', 'high', 'critical'] as const;
        const rawStatus = (p?.status ?? 'planning') as string;
        const rawPriority = (p?.priority ?? 'medium') as string;
        const status: ProjectStatus = (statusValues as readonly string[]).includes(rawStatus)
          ? (rawStatus as ProjectStatus)
          : 'planning';
        const priority: ProjectPriority = (priorityValues as readonly string[]).includes(rawPriority)
          ? (rawPriority as ProjectPriority)
          : 'medium';
        setForm({
          name: p?.name || '',
          description: p?.description || '',
          status,
          priority,
        });
      } catch (e: any) {
        setError(e?.response?.data?.message || 'Failed to load project');
      } finally {
        setLoading(false);
      }
    };
    fetchProject();
  }, [id]);

  // Prefill admin selector with the project's company when available
  useEffect(() => {
    if (isAdmin && initialCompanyId) {
      setSelectedCompanyId(initialCompanyId);
    }
  }, [isAdmin, initialCompanyId, setSelectedCompanyId]);

  const handleChange = (key: keyof ProjectForm) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm(prev => ({ ...prev, [key]: e.target.value }));
  };

  const handleSelect = (key: keyof ProjectForm) => (e: any) => {
    const value = e.target.value as string;
    if (key === 'status') {
      setForm(prev => ({ ...prev, status: value as ProjectStatus }));
    } else if (key === 'priority') {
      setForm(prev => ({ ...prev, priority: value as ProjectPriority }));
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      if (!id) throw new Error('Missing project ID');
      const payload: any = {
      name: form.name,
      description: form.description,
      status: form.status,
      priority: form.priority,
      };
      // Include company when admin selects one (backend currently ignores on update)
      if (isAdmin && selectedCompanyId) {
        payload.company = selectedCompanyId;
      }
      await projectsService.update(id, payload);
      navigate(`/projects/${id}`);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to update project');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Loading project...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Button variant="text" startIcon={<ArrowBack />} onClick={() => navigate(`/projects/${id}`)}>
          Back to Details
        </Button>
        <Typography variant="h4" component="h1" sx={{ flexGrow: 1 }}>
          Edit Project
        </Typography>
        <Button variant="contained" startIcon={<Save />} onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2, flexDirection: 'column' }}>
            <TextField
              label="Project Name"
              value={form.name}
              onChange={handleChange('name')}
              required
            />

            <TextField
              label="Description"
              value={form.description}
              onChange={handleChange('description')}
              multiline
              rows={3}
              required
            />

            <TextField
              label="Status"
              select
              value={form.status}
              onChange={handleSelect('status')}
            >
              {statusOptions.map(opt => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              label="Priority"
              select
              value={form.priority}
              onChange={handleSelect('priority')}
            >
              {priorityOptions.map(opt => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>

            {/* Company selector for Admins */}
            {isAdmin ? (
              <FormControl>
                <InputLabel>Company</InputLabel>
                <Select
                  label="Company"
                  value={selectedCompanyId || ''}
                  onChange={(e) => setSelectedCompanyId(e.target.value)}
                >
                  {companies.map((c) => (
                    <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : (
              <TextField
                label="Company"
                value={initialCompanyId ? 'Company assigned automatically' : 'Company will be assigned automatically'}
                disabled
                helperText="Your company is auto-populated based on your account"
              />
            )}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ProjectEdit;